import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { Box, CheckIcon, HStack, IconButton, Pressable, useTheme, VStack } from 'native-base';
import { find, startCase, toLower } from 'lodash';
import { User } from 'lucide-react';

import { FileAttachmentIcon, Modal, Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { AddUpdateCleaningTaskAssignment } from '@pimm/services/lib/sms-workforce/services';
import {
  AddUpdateTaskAssignmentRequest,
  AggAssignedTaskDto,
  CleaningScheduleTypeEnum,
  CleaningTaskStatusEnum,
} from '@pimm/services/lib/sms-workforce';
import { formatTo, formatToISOString, stringToDateLocal } from '@app/utils/date-formatter';
import { useSiteTime } from '@app/features/store-core';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { ConfirmIncompleteTask } from './confirm-incomplete-task';
import { GetPositionGuideDataLiveKey, useGetPositionGuideDataLive } from '../hooks/get-position-guide-live.hook';
import { getScheduleTypeColor } from '../_helper';

type GetPositionGuideDataLiveReturn = ReturnType<typeof useGetPositionGuideDataLive>[0];

type CleaningTasksItemProps = {
  cleaningTask: AggAssignedTaskDto;
  employeeId?: string;
  empName?: string;
  scheduleType: CleaningScheduleTypeEnum;
  siteId?: string;
  slotHasEmployee?: boolean;
  onPressLinkDocument?: (task: AggAssignedTaskDto) => void;
};

export const CleaningTasksItem = ({
  cleaningTask,
  employeeId,
  empName,
  scheduleType,
  siteId,
  slotHasEmployee = false,
  ...props
}: CleaningTasksItemProps) => {
  const queryClient = useQueryClient();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { translate } = useAppLocale();
  const siteTime = useSiteTime();
  const isCompleted = useBoolean(cleaningTask?.cleaningTaskStatus === CleaningTaskStatusEnum.Complete);
  const modalConfirm = useModalFocus();

  const addUpdateCleaningTask = useMutation({ mutationFn: AddUpdateCleaningTaskAssignment });

  const isNa = cleaningTask.cleaningTaskStatus === CleaningTaskStatusEnum.NotApplicable;
  const canInteract = !isNa && slotHasEmployee;
  const scheduleColor = getScheduleTypeColor(scheduleType);

  const dueDate = stringToDateLocal(cleaningTask.dueDate);
  const isOverDue = !!dueDate && dueDate < siteTime.today();
  const statusColor = isCompleted.value ? 'gray.500' : isOverDue ? 'error.500' : 'black';

  const handlePressLinkedDocument = () => {
    if (props.onPressLinkDocument) props.onPressLinkDocument(cleaningTask);
  };

  const handleCompletion = () => {
    const cleaningTaskStatus = isCompleted.value ? CleaningTaskStatusEnum.Incomplete : CleaningTaskStatusEnum.Complete;
    const statusDateISO = formatToISOString(siteTime.today());

    modalConfirm.setHide();

    if (dueDate && siteId) {
      const payload: AddUpdateTaskAssignmentRequest = {
        id: cleaningTask.cleaningTaskAssignmentId,
        cleaningTaskId: cleaningTask.cleaningTaskId,
        cleaningTaskStatus: cleaningTaskStatus,
        dueDate: formatToISOString(dueDate),
        employeeId: employeeId,
        scheduleType: scheduleType,
        siteId: siteId,
        statusDate: statusDateISO,
      };
      addUpdateCleaningTask.mutate(payload);

      const queryCache = queryClient.getQueryCache();
      const queryPositionGuide = find(queryCache.getAll(), _ => _.queryKey[0] === GetPositionGuideDataLiveKey);
      if (queryPositionGuide) {
        queryClient.setQueryData<GetPositionGuideDataLiveReturn['data']>(
          queryPositionGuide.queryKey,
          (prevState: GetPositionGuideDataLiveReturn['data']) => {
            const updatedCleaningTasks = prevState?.cleaningTasks?.map(_ => ({
              ..._,
              scheduleTypes: _.scheduleTypes?.map(st => ({
                ...st,
                assignedTasks: st?.assignedTasks?.map(task => {
                  if (
                    task.cleaningTaskId === cleaningTask.cleaningTaskId &&
                    task.cleaningTaskAssignmentId === cleaningTask.cleaningTaskAssignmentId
                  ) {
                    return { ...task, cleaningTaskStatus, statusDate: statusDateISO };
                  }
                  return task;
                }),
              })),
            }));

            const updatedPositionCleaningTasks = prevState?.positionCleaningTasks?.map(entry => ({
              ...entry,
              tasks: entry.tasks?.map(task => {
                if (task.cleaningTaskId === cleaningTask.cleaningTaskId) {
                  return { ...task, cleaningTaskStatus, statusDate: statusDateISO };
                }
                return task;
              }),
            }));

            return {
              ...prevState,
              cleaningTasks: updatedCleaningTasks,
              positionCleaningTasks: updatedPositionCleaningTasks,
            };
          },
        );
      }
    }
    isCompleted.toggle();
  };

  return (
    <HStack
      space={2}
      rounded="lg"
      p={2}
      alignItems="center"
      borderWidth={1}
      borderColor={isCompleted.value ? `${scheduleColor}.200` : 'gray.200'}
      bg={isCompleted.value ? `${scheduleColor}.50` : 'white'}
      minHeight="70px"
      opacity={isNa ? 0.7 : 1}
    >
      <Pressable
        w={7}
        h={7}
        alignItems="center"
        justifyContent="center"
        borderColor={isNa ? 'gray.200' : isCompleted.value ? `${scheduleColor}.500` : 'gray.300'}
        borderWidth={1}
        bg="white"
        rounded="lg"
        isDisabled={!canInteract}
        _disabled={{ borderColor: 'gray.100' }}
        _hover={{ borderColor: isCompleted.value ? `${scheduleColor}.500` : 'gray.500' }}
        onPress={canInteract ? (isCompleted.value ? modalConfirm.setOpen : handleCompletion) : undefined}
      >
        {({ isHovered }) => {
          if (isNa) return <Box w={3} h="2px" bg="gray.400" />;
          return (isHovered || isCompleted.value) && <CheckIcon size={6} color={isCompleted.value ? `${scheduleColor}.500` : 'gray.300'} />;
        }}
      </Pressable>

      <VStack space={0.5} flex={1} pl={1}>
        <Text
          size="md"
          fontWeight={600}
          color={isCompleted.value ? 'gray.500' : 'black'}
          lineHeight="xs"
          numberOfLines={3}
          ellipsizeMode="tail"
          textDecorationLine={isNa ? 'line-through' : 'none'}
        >
          {translate(cleaningTask?.title, cleaningTask.titleTranslations)}
        </Text>

        <HStack space={1} alignItems="flex-end">
          <Text size="sm" fontWeight={500} color={isCompleted.value ? 'gray.500' : 'gray.600'} lineHeight="xs">
            {translate(cleaningTask?.categoryTitle, cleaningTask?.categoryTitleTranslations)}
          </Text>
          {!!cleaningTask?.positionTitle && (
            <>
              <Text size="sm" fontWeight={500} color={isCompleted.value ? 'gray.500' : 'gray.600'} lineHeight="xs">
                \
              </Text>
              <Text
                size="sm"
                fontWeight={500}
                color={isCompleted.value ? 'gray.500' : 'gray.600'}
                lineHeight="xs"
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {cleaningTask?.positionTitle}
              </Text>
            </>
          )}
        </HStack>

        <HStack space={1} alignItems="center">
          <User size={12} color={isCompleted.value ? colors.gray[400] : colors.gray[500]} />
          {empName ? (
            <Text
              size="md"
              fontWeight={600}
              color={isCompleted.value ? colors.gray[500] : colors.black}
              lineHeight="none"
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {startCase(toLower(empName))}
            </Text>
          ) : (
            <Text size="md" fontWeight={500} color="gray.500" lineHeight="none" italic>
              {t('position_guide:unassigned')}
            </Text>
          )}
        </HStack>

        {isCompleted.value && !!cleaningTask.statusDate && (
          <Text size="md" fontWeight={500} color="gray.500" lineHeight="xs" numberOfLines={1} ellipsizeMode="tail" italic>
            {t('position_guide:completed')}: {formatTo(cleaningTask.statusDate, 'ddd, MMM DD hh:mm A')}
          </Text>
        )}
      </VStack>

      <IconButton
        rounded="lg"
        p={1}
        w={6}
        h={6}
        color="primary.500"
        isDisabled={!cleaningTask.linkedDoc}
        _disabled={{ _icon: { color: 'gray.500' } }}
        _hover={{ bg: undefined }}
        icon={<FileAttachmentIcon size="sm" />}
        onPress={handlePressLinkedDocument}
      />

      <Box width={55}>
        {!!dueDate && (
          <Text size="sm" fontWeight={700} color={statusColor} lineHeight="sm">
            <Text size="md" fontWeight={700} color={statusColor} lineHeight="2xs">
              {formatTo(dueDate, 'ddd')}
            </Text>
            {`\n`}
            {formatTo(dueDate, 'MMM DD')}
            {`\n`}
            {formatTo(dueDate, 'hh:mm A')}
          </Text>
        )}
      </Box>

      <Modal
        hideClose
        isOpen={modalConfirm.isOpen}
        onClose={modalConfirm.setHide}
        size="md"
        _content={{ rounded: 'xl', p: 5, pt: 4, maxW: 400 }}
      >
        <ConfirmIncompleteTask onClose={modalConfirm.setHide} onConfirm={handleCompletion} />
      </Modal>
    </HStack>
  );
};
